import React, {useMemo, useState} from 'react';
import { useRecoilState, useRecoilValue } from "recoil";
import BaseModal from "./BaseModal"
import activeModalState from "../../state/ui/activeModalState";
import FormControl from "../../components/FormControl";
import windowDimensionState from "../../state/ui/windowDimensionState";
import iconPollPinned from "../../../images/space/icon-poll-pinned.png"
import {FieldArray, FormikProvider, useFormik, Field} from "formik";
import firebaseApp from "../../services/firebase";
import firebase from "firebase";
import useTiming from "../../hooks/useTiming";
import {firebaseUidState} from "../../state/firebaseState";
import { useTranslation } from 'react-i18next';

const PollActionModal = () => {
  const timing = useTiming()
  const firebaseUid = useRecoilValue(firebaseUidState);
  const {t} = useTranslation('booth')

  const [activeModal, setShowModal] = useRecoilState(activeModalState);

  const sectionTitleClassName = "capitalize font-semibold mb-1"
  const outlineButtonClassName = "bg-gray-50 hover:bg-gray-100 border border-gray-300 ease-in duration-100 flex items-center justify-center"

  const msgRef = useMemo(() => firebaseApp.database().ref(`room-questions/${timing.id}`), [timing?.id, firebaseUid]);

  const formik = useFormik({
    initialValues: {
      label: "",
      choices: [""],
      seconds: 30, 
    },
    validate: (values) => {
      const errors = {};
      if(values.label.length <= 3) {
        errors.label = "The title must have at least than 4 characters";
      }
      if(values.choices.length === 0) {
        errors.choices = "Please specify at least one option";
      }
      if(values.seconds <= 5) {
        errors.seconds = "Poll duration must be at least 6 seconds";
      }
      values.choices.forEach((item, index) => {
        if(item.length === 0) {
          errors[`choiceItem${index}`] = "An option cannot be empty";
        }
      })
      return errors;
    },

    onSubmit: (values, {setSubmitting, resetForm}) => {
      const newMessage = msgRef.push();

      const messageObj = {
        userId: firebaseUid,
        timestamp: firebase.database.ServerValue.TIMESTAMP,
        type: 'button',
        layout: 'pinned',
        label: values.label,
        duration: values.seconds,
        choices: {},
      }
      values.choices.forEach(choice => {
        messageObj.choices[msgRef.child(newMessage.key).child('choices').push().key] = {
          label: choice,
        };
      });

      return newMessage.setWithPriority(messageObj, firebase.database.ServerValue.TIMESTAMP).then(() => {
        resetForm();
        setSubmitting(false);
        setShowModal('')
      })
    }
  })

  return (
    <BaseModal title={t('create-new-poll')} show={activeModal === 'poll'} showTitle center autoHeight extraLarge titleCloseButton>
      <FormikProvider value={formik}>
        <div className="w-full relative flex flex-col space-y-4 mb-8">
          <FormControl.Default inputClass="input-control" htmlFor="name" label={t('question-title')} error={formik.errors.label}>
            <input
              type="text"
              placeholder=""
              {...formik.getFieldProps('label')}
            />
          </FormControl.Default>
          <FormControl.Select
            light
            label={t('poll-type')}
            className="mb-2"
            options={[t('multiple-option')]}
            value={t('multiple-option')}
            disabled={true}
          />
          <div>
            <h4 className={sectionTitleClassName}>{t('options')}</h4>
              {/* List of options */}

            <FieldArray
              name="choices"
              render={arrayHelpers => (
                <div className="flex flex-col space-y-2">
                  {formik.values.choices.map((choice, index) => (
                    <div key={index}>
                      <div className="flex items-center">
                        <div className="w-11/12">
                          <FormControl.Default inputClass="input-control" XhtmlFor={`option-${index + 1}`} label={`${t('option')} ${index + 1}`}>
                            <Field name={`choices.${index}`} type="text" />
                          </FormControl.Default>
                        </div>
                        <div className="w-1/12 flex items-center justify-center">
                          <button className={`rounded-full h-8 w-8 ${outlineButtonClassName}`} onClick={() => arrayHelpers.remove(index)}>
                            <i className="fas fa-minus fa-fw text-lg"></i>
                          </button>
                        </div>
                      </div>
                      {formik.errors[`choiceItem${index}`] && <p data-error={true} className={'w-full text-error text-xs'}>{formik.errors[`choiceItem${index}`]}</p>}
                    </div>
                    ))}
                  <button className={`w-11/12 py-2 rounded-lg ${outlineButtonClassName}`} onClick={() => arrayHelpers.push('')}>
                    <i className="fas fa-plus fa-fw"></i>
                    {t('add-new-option')}
                  </button>
                  {formik.errors.choices && <p data-error={true} className={'text-error text-xs'}>{formik.errors.choices}</p>}
                </div>
              )}
            />
          </div>
          <div>
            <h4 className={sectionTitleClassName}>{t('poll-duration-question')}</h4>
            <FormControl.Default inputClass="input-control" htmlFor="name" label={t('seconds')} error={formik.errors.seconds}>
              <input
                type="number"
                placeholder=""
                {...formik.getFieldProps('seconds')}
                min={5}
              />
            </FormControl.Default>
          </div>
          <div>
            <h4 className={sectionTitleClassName}>{t('poll-type-question')}</h4>
            <ul className="poll-type-options grid grid-cols-3 gap-3">
              <li className="option">
                <input type="radio" value="pinned" name="poll_type" id="answer_pinned" checked/>
                <label htmlFor="answer_pinned">{t('pinned')}</label>
                <div>
                  <img src={iconPollPinned} className="h-20 w-20 object-contain"/>
                  <p>
                    {t('pinned')}
                  </p>
                </div>
              </li>
            </ul>
          </div>
        </div>
        <div className="w-full relative">
          <button className="w-full btn-primary" onClick={formik.submitForm}>{t('create-poll')}</button>
        </div>
      </FormikProvider>
    </BaseModal>
  )
}

export default PollActionModal
